import { Box, Button } from "@mui/material";
import type { JSX, ReactNode } from "react";

/**
 * Universal pagination row used by every list/table view in the
 * dashboard (Support, CRM, Marketing, Admin, Sales, Documentation,
 * Server Status).
 *
 * Renders:
 *
 *   Showing 1–25 of 47 [tickets]      Rows [25 ▾]   ‹ Prev | Page 1 of 2 | Next ›
 *
 * - The per-page selector is optional. Pages that don't want to expose
 *   it omit `onPerPageChange`; the selector disappears. It stays a NATIVE
 *   <select> (themed via sx) so list-view tests can drive it.
 * - `entityNoun` (e.g. "tickets", "customers") is appended to the
 *   count for human context. Omit it to render just the number.
 * - `hideIfSinglePage` collapses the whole bar to `null` when
 *   `totalPages <= 1`. Default `false`.
 * - Returns `null` when `totalCount === 0` (no rows to paginate).
 */
export interface PaginationProps {
  /** Current page, 1-indexed. */
  page: number;
  /** Rows per page. */
  perPage: number;
  /** Total number of rows across all pages. */
  totalCount: number;
  /**
   * Total number of pages. If omitted, derived from
   * `Math.max(1, Math.ceil(totalCount / perPage))`.
   */
  totalPages?: number;
  /** Called when the user clicks Previous / Next. */
  onPageChange: (page: number) => void;
  /** Optional — passing it shows the per-page selector. */
  onPerPageChange?: (perPage: number) => void;
  /** Options for the per-page selector. */
  perPageOptions?: number[];
  /**
   * Plural noun appended to the count for context, e.g.
   * "Showing 1–25 of 47 customers". Omitted → no noun.
   */
  entityNoun?: string;
  /** Hide the entire bar when `totalPages <= 1`. Default `false`. */
  hideIfSinglePage?: boolean;
}

const DEFAULT_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const strong = { fontWeight: 500, color: "text.primary" } as const;

export function Pagination({
  page,
  perPage,
  totalCount,
  totalPages: totalPagesProp,
  onPageChange,
  onPerPageChange,
  perPageOptions = DEFAULT_PER_PAGE_OPTIONS,
  entityNoun,
  hideIfSinglePage = false,
}: PaginationProps): JSX.Element | null {
  if (totalCount === 0) return null;

  const totalPages = Math.max(
    1,
    totalPagesProp ?? Math.ceil(totalCount / Math.max(1, perPage)),
  );

  if (hideIfSinglePage && totalPages <= 1) return null;

  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, totalCount);

  const showSelector = typeof onPerPageChange === "function";

  return (
    <Box
      data-testid="pagination"
      sx={{
        mt: 1.5,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        fontSize: 12,
        color: "text.disabled",
      }}
    >
      <Box component="span">
        Showing <Box component="span" sx={strong}>{from}</Box>–
        <Box component="span" sx={strong}>{to}</Box> of{" "}
        <Box component="span" sx={strong}>{totalCount}</Box>
        {entityNoun ? <Noun noun={entityNoun} /> : null}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        {showSelector && (
          <Box component="label" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            Rows
            <Box
              component="select"
              value={perPage}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                onPerPageChange?.(Number(e.target.value))
              }
              aria-label="Rows per page"
              sx={{
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
                bgcolor: "background.default",
                color: "text.primary",
                px: 1,
                py: 0.5,
                fontSize: 12,
                cursor: "pointer",
                "&:hover": { borderColor: "text.disabled" },
              }}
            >
              {perPageOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            size="small"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            aria-label="Previous page"
            sx={{ minWidth: 0, px: 1, py: 0.5, fontSize: 12 }}
          >
            ‹ Prev
          </Button>
          <Box component="span" sx={{ px: 1 }}>
            Page <Box component="span" sx={strong}>{page}</Box> of{" "}
            <Box component="span" sx={strong}>{totalPages}</Box>
          </Box>
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            size="small"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            aria-label="Next page"
            sx={{ minWidth: 0, px: 1, py: 0.5, fontSize: 12 }}
          >
            Next ›
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

function Noun({ noun }: { noun: string }): ReactNode {
  return <> {noun}</>;
}

/**
 * Rails / CRM convention for paginated responses. Two shapes coexist in
 * the stack and this interface accepts BOTH so one helper can map them:
 *
 *   - CRM + brain Rails APIs and the dashboard's own SQLite endpoints
 *     send `current_page`.
 *   - The support-center portal-api's `render_paginated` → `pagination_meta`
 *     sends `page` + `per_page` (no `current_page`).
 *
 * Reading only `current_page` against the portal-api shape yields
 * `undefined`, and `(undefined - 1) * perPage + 1` is `NaN` — that was
 * the "Showing NaN–NaN of N" bug on the Support → Organizations list.
 */
export interface PaginationMeta {
  /** CRM / brain convention. */
  current_page?: number;
  /** portal-api (`render_paginated`) convention — alias of current_page. */
  page?: number;
  total_pages?: number;
  total_count?: number;
  per_page?: number;
}

/**
 * Maps a Rails-style meta object onto the flat `<Pagination>` props.
 * Tolerates both the `current_page` (CRM/brain) and `page` (portal-api)
 * keys, and always returns real numbers — never `undefined`/`NaN` — so a
 * missing field degrades to a sane first page instead of breaking the
 * row-range maths. Use this at any call site holding a paginated
 * response from React Query / a Redux slice — keeps the JSX a single
 * line.
 */
export function paginationPropsFromMeta(
  meta: PaginationMeta | null | undefined,
): {
  page: number;
  perPage: number;
  totalCount: number;
  totalPages: number;
} {
  return {
    page: meta?.current_page ?? meta?.page ?? 1,
    perPage: meta?.per_page ?? 25,
    totalCount: meta?.total_count ?? 0,
    totalPages: meta?.total_pages ?? 1,
  };
}
